import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle, XCircle, ExternalLink, Settings, Code, Zap, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { testApiConnection, type ApiConnectorConfig } from '@/integrations/apiConnector';

interface APIConnectorModalProps {
  onConnect: (config: ApiConnectorConfig) => Promise<void>;
  children: React.ReactNode;
}

interface EnhancedApiConfig extends ApiConnectorConfig {
  retryAttempts: number;
  timeout: number;
  customPayload: boolean;
  payloadTemplate: string;
  dataTransformation: boolean;
  fieldMapping: Array<{source: string; target: string; enabled: boolean}>;
  webhookEvents: string[];
  rateLimit: {
    enabled: boolean;
    requestsPerMinute: number;
  };
}

export function APIConnectorModal({ onConnect, children }: APIConnectorModalProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testMessage, setTestMessage] = useState('');
  
  const [config, setConfig] = useState<EnhancedApiConfig>({
    url: '',
    method: 'POST',
    auth: { type: 'none' },
    retryAttempts: 3,
    timeout: 30000,
    customPayload: false,
    payloadTemplate: '',
    dataTransformation: false,
    fieldMapping: [
      { source: 'invoiceNumber', target: 'invoice_number', enabled: true },
      { source: 'supplier', target: 'supplier_name', enabled: true },
      { source: 'amountDue', target: 'total_amount', enabled: true },
    ],
    webhookEvents: ['bill.parsed', 'bill.exported'],
    rateLimit: {
      enabled: false,
      requestsPerMinute: 60
    }
  });

  const handleTestConnection = async () => {
    if (!config.url) {
      toast.error('Please enter an endpoint URL');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setTestMessage('');

    try {
      await testApiConnection(config);
      setTestResult('success');
      setTestMessage('Connection successful!');
      toast.success('API connection test passed');
    } catch (error: any) {
      setTestResult('error');
      setTestMessage(error.message || 'Connection failed');
      toast.error('API connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!config.url) {
      toast.error('Please enter an endpoint URL');
      return;
    }

    setLoading(true);
    try {
      // Convert enhanced config to basic config for the API
      const basicConfig: ApiConnectorConfig = {
        url: config.url,
        method: config.method,
        auth: config.auth,
        extraHeaders: config.extraHeaders
      };
      
      await onConnect(basicConfig);
      setOpen(false);
      toast.success('API configuration saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  const addFieldMapping = () => {
    setConfig(prev => ({
      ...prev,
      fieldMapping: [...prev.fieldMapping, { source: '', target: '', enabled: true }]
    }));
  };

  const updateFieldMapping = (index: number, field: 'source' | 'target' | 'enabled', value: any) => {
    setConfig(prev => ({
      ...prev,
      fieldMapping: prev.fieldMapping.map((mapping, i) => 
        i === index ? { ...mapping, [field]: value } : mapping
      )
    }));
  };

  const removeFieldMapping = (index: number) => {
    setConfig(prev => ({
      ...prev,
      fieldMapping: prev.fieldMapping.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5 text-blue-600" />
            Connect to External API
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Send your parsed bill data to an external API endpoint with advanced configuration
          </p>
        </DialogHeader>
        
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Basic Setup
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Advanced
            </TabsTrigger>
            <TabsTrigger value="transformation" className="flex items-center gap-2">
              <Code className="h-4 w-4" />
              Data Transform
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6 mt-6">
            {/* Basic Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ExternalLink className="h-5 w-5" />
                  API Endpoint Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="url">Endpoint URL *</Label>
                    <Input
                      id="url"
                      placeholder="https://api.example.com/webhook"
                      value={config.url}
                      onChange={(e) => setConfig(prev => ({ ...prev, url: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="method">HTTP Method</Label>
                    <Select
                      value={config.method}
                      onValueChange={(value: 'POST' | 'PUT') => setConfig(prev => ({ ...prev, method: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="PATCH">PATCH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Authentication */}
                <div className="space-y-2">
                  <Label>Authentication</Label>
                  <Select
                    value={config.auth?.type || 'none'}
                    onValueChange={(value: 'none' | 'bearer' | 'header') => 
                      setConfig(prev => ({ 
                        ...prev, 
                        auth: { type: value, token: '', headerName: '', headerValue: '' }
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="bearer">Bearer Token</SelectItem>
                      <SelectItem value="header">Custom Header</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {config.auth?.type === 'bearer' && (
                  <div className="space-y-2">
                    <Label htmlFor="token">Bearer Token</Label>
                    <Input
                      id="token"
                      type="password"
                      placeholder="your-api-token"
                      value={config.auth.token || ''}
                      onChange={(e) => setConfig(prev => ({ 
                        ...prev, 
                        auth: { ...prev.auth!, token: e.target.value }
                      }))}
                    />
                  </div>
                )}

                {config.auth?.type === 'header' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="headerName">Header Name</Label>
                      <Input
                        id="headerName"
                        placeholder="X-API-Key"
                        value={config.auth.headerName || ''}
                        onChange={(e) => setConfig(prev => ({ 
                          ...prev, 
                          auth: { ...prev.auth!, headerName: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="headerValue">Header Value</Label>
                      <Input
                        id="headerValue"
                        type="password"
                        placeholder="your-header-value"
                        value={config.auth.headerValue || ''}
                        onChange={(e) => setConfig(prev => ({ 
                          ...prev, 
                          auth: { ...prev.auth!, headerValue: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                )}

                {/* Test Connection */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <h4 className="font-medium">Test Connection</h4>
                    <p className="text-sm text-muted-foreground">
                      Send a test payload to verify your configuration
                    </p>
                  </div>
                  <Button
                    onClick={handleTestConnection}
                    disabled={testing || !config.url}
                    variant="outline"
                  >
                    {testing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="h-4 w-4 mr-2" />
                    )}
                    Test
                  </Button>
                </div>
                
                {testResult && (
                  <div className="flex items-center gap-2">
                    {testResult === 'success' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`text-sm ${testResult === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {testMessage}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6 mt-6">
            {/* Advanced Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Advanced Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Performance Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium">Performance Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="retryAttempts">Retry Attempts</Label>
                      <Input
                        id="retryAttempts"
                        type="number"
                        min="0"
                        max="10"
                        value={config.retryAttempts}
                        onChange={(e) => setConfig(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="timeout">Timeout (ms)</Label>
                      <Input
                        id="timeout"
                        type="number"
                        min="1000"
                        max="60000"
                        value={config.timeout}
                        onChange={(e) => setConfig(prev => ({ ...prev, timeout: parseInt(e.target.value) || 30000 }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Rate Limiting */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Rate Limiting</h4>
                      <p className="text-sm text-muted-foreground">
                        Control the frequency of API calls
                      </p>
                    </div>
                    <Switch
                      checked={config.rateLimit.enabled}
                      onCheckedChange={(checked) => 
                        setConfig(prev => ({ 
                          ...prev, 
                          rateLimit: { ...prev.rateLimit, enabled: checked }
                        }))
                      }
                    />
                  </div>
                  {config.rateLimit.enabled && (
                    <div className="space-y-2">
                      <Label htmlFor="requestsPerMinute">Requests per Minute</Label>
                      <Input
                        id="requestsPerMinute"
                        type="number"
                        min="1"
                        max="1000"
                        value={config.rateLimit.requestsPerMinute}
                        onChange={(e) => setConfig(prev => ({ 
                          ...prev, 
                          rateLimit: { ...prev.rateLimit, requestsPerMinute: parseInt(e.target.value) || 60 }
                        }))}
                      />
                    </div>
                  )}
                </div>

                {/* Extra Headers */}
                <div className="space-y-2">
                  <Label>Extra Headers (JSON)</Label>
                  <Textarea
                    placeholder='{"X-Custom-Header": "value", "X-Client-Version": "1.0"}'
                    rows={3}
                    onChange={(e) => {
                      try {
                        const headers = e.target.value ? JSON.parse(e.target.value) : {};
                        setConfig(prev => ({ ...prev, extraHeaders: headers }));
                      } catch {
                        // Invalid JSON, ignore
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transformation" className="space-y-6 mt-6">
            {/* Data Transformation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Data Transformation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Custom Payload */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Custom Payload Template</h4>
                      <p className="text-sm text-muted-foreground">
                        Define a custom JSON structure for the API payload
                      </p>
                    </div>
                    <Switch
                      checked={config.customPayload}
                      onCheckedChange={(checked) => 
                        setConfig(prev => ({ ...prev, customPayload: checked }))
                      }
                    />
                  </div>
                  {config.customPayload && (
                    <div className="space-y-2">
                      <Label>Payload Template (JSON)</Label>
                      <Textarea
                        placeholder={`{
  "invoice": {
    "number": "{{invoiceNumber}}",
    "date": "{{invoiceDate}}",
    "supplier": "{{supplier}}",
    "amount": {{amountDue}}
  },
  "metadata": {
    "source": "bill-parser",
    "timestamp": "{{timestamp}}"
  }
}`}
                        rows={8}
                        value={config.payloadTemplate}
                        onChange={(e) => setConfig(prev => ({ ...prev, payloadTemplate: e.target.value }))}
                      />
                    </div>
                  )}
                </div>

                {/* Field Mapping */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Field Mapping</h4>
                    <Button onClick={addFieldMapping} size="sm" variant="outline">
                      Add Mapping
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {config.fieldMapping.map((mapping, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 border rounded-lg">
                        <Switch
                          checked={mapping.enabled}
                          onCheckedChange={(checked) => 
                            updateFieldMapping(index, 'enabled', checked)
                          }
                        />
                        <Input
                          placeholder="Source Field"
                          value={mapping.source}
                          onChange={(e) => updateFieldMapping(index, 'source', e.target.value)}
                          className="flex-1"
                        />
                        <span className="text-gray-400">→</span>
                        <Input
                          placeholder="Target Field"
                          value={mapping.target}
                          onChange={(e) => updateFieldMapping(index, 'target', e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          onClick={() => removeFieldMapping(index)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={loading || !config.url}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Save Configuration
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
